import { fitnessLocalWorkoutId } from './fitnessLocalWorkoutDomain.ts';

export const FITNESS_REST_SKIP_TOOL = 'propose_fitness_rest_skip';
export interface FitnessRestSkipState {
  runtimeId: string; revision: number; phase: 'rest'; paused: false; busy: false;
  workoutName: string | null; exerciseName: string; exerciseIndex: number; currentSet: number;
  sets: number; restDurationSeconds: number; remainingMs: number; countdownMs: 3200;
}
export interface FitnessRestSkipEnvelope {
  version: 1; owner: string; observedAt: string; status: 'ready' | 'unavailable'; rest: FitnessRestSkipState | null;
}
export interface FitnessRestSkipArgs { action: 'skip_current_rest'; snapshot: FitnessRestSkipEnvelope }
const fail = (): never => { throw new Error('invalid_fitness_rest_skip'); };
const object = (value: unknown): value is Record<string, unknown> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  if (prototype === null) return true;
  const constructor = Object.getOwnPropertyDescriptor(prototype, 'constructor');
  if (Object.getPrototypeOf(prototype) !== null || !constructor || !('value' in constructor) || typeof constructor.value !== 'function'
    || Function.prototype.toString.call(constructor.value) !== Function.prototype.toString.call(Object)) return false;
  const nativePrototype = Object.getOwnPropertyDescriptor(constructor.value, 'prototype');
  return !!nativePrototype && 'value' in nativePrototype && nativePrototype.value === prototype;
};
/** Only exact own enumerable data keys: public schema validation is not private runtime authority. */
const dataKeys = (value: Record<string, unknown>, allowed: readonly string[]) => {
  const keys = Reflect.ownKeys(value);
  return keys.length === allowed.length && keys.every(key => {
    if (typeof key !== 'string' || !allowed.includes(key)) return false;
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    return !!descriptor && descriptor.enumerable === true && 'value' in descriptor;
  });
};
const integer = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max;
const finite = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
const name = (value: unknown): value is string => typeof value === 'string' && value.length > 0 && value.length <= 200 && value === value.trim() && !/[\p{Cc}\p{Cf}\p{Cs}]/u.test(value);

/** Timestamped approximation only; the effective deadline, inventory and clock remain private. */
export function canonicalFitnessRestSkipEnvelope(input: unknown, owner: string, now: Date = new Date()): FitnessRestSkipEnvelope {
  if (!fitnessLocalWorkoutId(owner) || !Number.isFinite(now.getTime()) || !object(input) || !dataKeys(input, ['version', 'owner', 'observedAt', 'status', 'rest']) || input.version !== 1 || input.owner !== owner) return fail();
  if (typeof input.observedAt !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(input.observedAt)) return fail();
  const observed = Date.parse(input.observedAt);
  if (!Number.isFinite(observed) || new Date(observed).toISOString() !== input.observedAt || now.getTime() - observed > 300000 || observed - now.getTime() > 30000) return fail();
  if (input.status !== 'ready' && input.status !== 'unavailable') return fail();
  if (input.status === 'unavailable') {
    if (input.rest !== null) return fail();
    return { version: 1, owner, observedAt: input.observedAt, status: 'unavailable', rest: null };
  }
  const rest = input.rest;
  if (!object(rest) || !dataKeys(rest, ['runtimeId', 'revision', 'phase', 'paused', 'busy', 'workoutName', 'exerciseName', 'exerciseIndex', 'currentSet', 'sets', 'restDurationSeconds', 'remainingMs', 'countdownMs'])
    || !fitnessLocalWorkoutId(rest.runtimeId) || !integer(rest.revision, 0, Number.MAX_SAFE_INTEGER) || rest.paused !== false || rest.busy !== false
    || rest.phase !== 'rest' || rest.workoutName !== null && !name(rest.workoutName) || !name(rest.exerciseName)
    || !integer(rest.exerciseIndex, 0, 100000) || !integer(rest.currentSet, 2, 32) || !integer(rest.sets, 2, 32) || rest.currentSet > rest.sets
    || !finite(rest.restDurationSeconds, 0, 86400) || !finite(rest.remainingMs, 0, 86400000) || rest.remainingMs <= 0 || rest.countdownMs !== 3200) return fail();
  const result: FitnessRestSkipEnvelope = { version: 1, owner, observedAt: input.observedAt, status: 'ready', rest: {
    runtimeId: rest.runtimeId, revision: rest.revision, phase: 'rest', paused: false, busy: false, workoutName: rest.workoutName as string | null,
    exerciseName: rest.exerciseName, exerciseIndex: rest.exerciseIndex, currentSet: rest.currentSet, sets: rest.sets,
    restDurationSeconds: rest.restDurationSeconds, remainingMs: rest.remainingMs, countdownMs: 3200,
  } };
  if (new TextEncoder().encode(JSON.stringify(result)).length > 4096) return fail();
  return result;
}

/** Review arguments cannot replace the dedicated controller-issued snapshot or its live clock checks. */
export function canonicalFitnessRestSkipArgs(input: unknown, owner: string, now: Date = new Date()): FitnessRestSkipArgs {
  if (!object(input) || !dataKeys(input, ['action', 'snapshot']) || input.action !== 'skip_current_rest') return fail();
  const snapshot = canonicalFitnessRestSkipEnvelope(input.snapshot, owner, now);
  if (snapshot.status !== 'ready' || !snapshot.rest) return fail();
  return { action: 'skip_current_rest', snapshot };
}
